import React from 'react';
import { Bot, CheckCircle2, AlertCircle, Zap, Settings, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { AIAnalysisResponse, AIAnalysisResultItem } from '@/services/kbService';

export interface QAAIAnalysisModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: AIAnalysisResponse | null;
}

export const QAAIAnalysisModal: React.FC<QAAIAnalysisModalProps> = ({
  open,
  onOpenChange,
  result,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground max-w-[95vw] sm:max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            AI Entity Analysis Results
          </DialogTitle>
        </DialogHeader>

        {result && (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg border ${result.success ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-destructive/10 border-destructive/30'}`}>
              <div className="flex items-start gap-2">
                {result.success ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${result.success ? 'text-emerald-400' : 'text-red-400'}`}>
                    {result.success ? 'AI Analysis Complete' : 'AI Analysis Failed'}
                  </p>
                  <p className={`text-sm mt-1 ${result.success ? 'text-emerald-400' : 'text-red-400'}`}>
                    {result.message}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-muted-foreground">Total Analyzed</p>
                  <p className="text-xl font-bold">{result.total_analyzed}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-muted-foreground">Correct</p>
                  <p className="text-xl font-bold text-emerald-400">{result.correct_classifications}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-muted-foreground">Suggested Fixes</p>
                  <p className="text-xl font-bold text-amber-500">{result.suggested_corrections}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-muted-foreground">High Confidence</p>
                  <p className="text-xl font-bold text-red-400">{result.high_confidence_corrections}</p>
                </CardContent>
              </Card>
            </div>

            <p className="text-sm text-muted-foreground">
              Cost: ${result.estimated_cost_usd.toFixed(2)} | Tokens: {result.total_tokens_used} | Time: {(result.processing_time_ms / 1000).toFixed(2)}s
            </p>

            {/* Smart Filtering Statistics */}
            {result.filter_stats && (
              <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="h-5 w-5 text-primary" />
                  <span className="font-medium text-primary">Smart Filtering Applied</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Total Candidates</p>
                    <p className="text-lg font-bold">{result.filter_stats.total_candidates}</p>
                  </div>
                  <div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <p className="text-xs text-muted-foreground">Skipped (High Conf)</p>
                            <p className="text-lg font-bold text-emerald-400 flex items-center gap-1">
                              {result.filter_stats.skipped_high_confidence}
                              <CheckCircle2 className="h-4 w-4" />
                            </p>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="dark">Entities with confidence ≥80% were skipped</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <p className="text-xs text-muted-foreground">Skipped (Patterns)</p>
                            <p className="text-lg font-bold text-primary flex items-center gap-1">
                              {result.filter_stats.skipped_pattern_match}
                              <Settings className="h-4 w-4" />
                            </p>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="dark">Entities resolved by pattern rules were skipped</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <p className="text-xs text-muted-foreground">Skipped (Voting)</p>
                            <p className="text-lg font-bold text-purple-400 flex items-center gap-1">
                              {result.filter_stats.skipped_majority_vote}
                              <Users className="h-4 w-4" />
                            </p>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="dark">Entities resolved by majority voting were skipped</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Sent to AI (Paid)</p>
                    <p className="text-lg font-bold text-amber-500 flex items-center gap-1">
                      {result.filter_stats.sent_to_ai}
                      <Bot className="h-4 w-4" />
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Estimated Cost Saved</p>
                    <p className="text-lg font-bold text-emerald-400">
                      ${result.filter_stats.cost_saved_usd.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Suggested Corrections Table */}
            {result.results && result.results.filter((r: AIAnalysisResultItem) => !r.is_correct).length > 0 && (
              <>
                <Separator />
                <h3 className="font-medium">Suggested Corrections</h3>
                <div className="overflow-x-auto border rounded-lg">
                  <Table>
                    <TableHeader className="bg-muted/30">
                      <TableRow>
                        <TableHead>Entity</TableHead>
                        <TableHead>Current Type</TableHead>
                        <TableHead>Suggested</TableHead>
                        <TableHead>Confidence</TableHead>
                        <TableHead>Reasoning</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.results
                        .filter((r: AIAnalysisResultItem) => !r.is_correct)
                        .map((item: AIAnalysisResultItem) => (
                          <TableRow key={item.entity_id}>
                            <TableCell className="font-medium max-w-[180px]">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="truncate block">{item.entity_text}</span>
                                  </TooltipTrigger>
                                  <TooltipContent className="dark">{item.entity_text}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-destructive/10 text-red-400 border-destructive/30">
                                {item.current_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                                {item.suggested_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  item.confidence === 'high'
                                    ? 'bg-emerald-500/10 text-emerald-400'
                                    : item.confidence === 'medium'
                                    ? 'bg-amber-500/10 text-amber-500'
                                    : 'bg-destructive/10 text-red-400'
                                }
                              >
                                {(item.confidence_score * 100).toFixed(0)}%
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[200px]">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="truncate block cursor-help">
                                      {item.reasoning && item.reasoning.length > 50
                                        ? `${item.reasoning.substring(0, 50)}...`
                                        : item.reasoning}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="dark max-w-md">{item.reasoning}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
